import { Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './store/auth';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import TicketsPage from './pages/TicketsPage';
import TicketDetailPage from './pages/TicketDetailPage';
import AgentsPage from './pages/AgentsPage';
import KbPage from './pages/KbPage';
import QueuesPage from './pages/QueuesPage';
import SlaPage from './pages/SlaPage';
import ApiKeysPage from './pages/ApiKeysPage';
import WebhooksPage from './pages/WebhooksPage';
import SettingsPage from './pages/SettingsPage';
import TenantsPage from './pages/TenantsPage';
import CustomerPortalPage from './pages/CustomerPortalPage';
import CustomerTicketDetailPage from './pages/CustomerTicketDetailPage';
import ProductManualPage from './pages/ProductManualPage';

function NavItem({ to, label }: { to: string; label: string }) {
  const loc = useLocation();
  const active = loc.pathname === to || (to !== '/' && loc.pathname.startsWith(to));
  return (
    <Link
      to={to}
      className={
        'block px-3 py-2 rounded-lg text-sm transition-colors ' +
        (active ? 'bg-brand-600 text-white' : 'text-slate-700 hover:bg-slate-100')
      }
    >
      {label}
    </Link>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { actor, tenant, clear } = useAuth();
  const nav = useNavigate();

  if (!actor) return <>{children}</>;

  const isCustomer = actor.type === 'END_CUSTOMER';
  const isSuper = actor.type === 'SUPER_ADMIN';

  return (
    <div className="min-h-screen flex">
      <aside className="w-60 border-r border-slate-200 bg-white p-4 flex flex-col gap-1">
        <div className="px-2 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-brand-500 text-white grid place-items-center font-bold">T</div>
            <div>
              <div className="font-semibold text-slate-900 leading-tight">SupportByte</div>
              <div className="text-[11px] text-slate-500">{tenant?.name ?? 'RunTechByte'}</div>
            </div>
          </div>
        </div>

        {isCustomer ? (
          <>
            <NavItem to="/portal" label="My tickets" />
            <NavItem to="/portal/new" label="New ticket" />
          </>
        ) : isSuper ? (
          <>
            <NavItem to="/" label="Overview" />
            <NavItem to="/tenants" label="Tenants" />
            <NavItem to="/tickets" label="Tickets (all)" />
          </>
        ) : (
          <>
            <NavItem to="/" label="Dashboard" />
            <NavItem to="/tickets" label="Tickets" />
            <NavItem to="/agents" label="Agents" />
            <NavItem to="/queues" label="Queues" />
            <NavItem to="/sla" label="SLA" />
            <NavItem to="/kb" label="Knowledge base" />
            <NavItem to="/manual" label="Product manual" />
            <NavItem to="/api-keys" label="API keys" />
            <NavItem to="/webhooks" label="Webhooks" />
            <NavItem to="/settings" label="Settings" />
          </>
        )}

        <div className="mt-auto pt-4 border-t border-slate-200">
          <div className="px-2 text-xs text-slate-500">Signed in as</div>
          <div className="px-2 text-sm font-medium text-slate-800">{actor.name}</div>
          <div className="px-2 text-[11px] text-slate-500 mb-2">{actor.type} · {actor.role}</div>
          <button
            className="btn-ghost w-full justify-start text-sm"
            onClick={() => { clear(); nav('/login'); }}
          >
            Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}

function Protected({ children, allow }: { children: React.ReactNode; allow?: Array<'SUPER_ADMIN' | 'OWNER' | 'AGENT' | 'END_CUSTOMER'> }) {
  const actor = useAuth((s) => s.actor);
  const loc = useLocation();
  if (!actor) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (allow && !allow.includes(actor.type)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  const actor = useAuth((s) => s.actor);
  return (
    <Shell>
      <Routes>
        <Route path="/login" element={actor ? <Navigate to="/" replace /> : <LoginPage />} />

        {/* Customer portal */}
        <Route path="/portal" element={<Protected allow={['END_CUSTOMER']}><CustomerPortalPage /></Protected>} />
        <Route path="/portal/new" element={<Protected allow={['END_CUSTOMER']}><CustomerPortalPage initialTab="new" /></Protected>} />
        <Route path="/portal/tickets/:id" element={<Protected allow={['END_CUSTOMER']}><CustomerTicketDetailPage /></Protected>} />

        {/* Staff app */}
        <Route path="/" element={<Protected><DashboardPage /></Protected>} />
        <Route path="/tickets" element={<Protected allow={['OWNER', 'AGENT', 'SUPER_ADMIN']}><TicketsPage /></Protected>} />
        <Route path="/tickets/:id" element={<Protected allow={['OWNER', 'AGENT', 'SUPER_ADMIN']}><TicketDetailPage /></Protected>} />
        <Route path="/agents" element={<Protected allow={['OWNER', 'AGENT', 'SUPER_ADMIN']}><AgentsPage /></Protected>} />
        <Route path="/queues" element={<Protected allow={['OWNER', 'AGENT', 'SUPER_ADMIN']}><QueuesPage /></Protected>} />
        <Route path="/sla" element={<Protected allow={['OWNER', 'AGENT', 'SUPER_ADMIN']}><SlaPage /></Protected>} />
        <Route path="/kb" element={<Protected allow={['OWNER', 'AGENT', 'SUPER_ADMIN']}><KbPage /></Protected>} />
        <Route path="/manual" element={<Protected allow={['OWNER', 'AGENT', 'SUPER_ADMIN']}><ProductManualPage /></Protected>} />
        <Route path="/api-keys" element={<Protected allow={['OWNER', 'SUPER_ADMIN']}><ApiKeysPage /></Protected>} />
        <Route path="/webhooks" element={<Protected allow={['OWNER', 'SUPER_ADMIN']}><WebhooksPage /></Protected>} />
        <Route path="/settings" element={<Protected allow={['OWNER', 'AGENT', 'SUPER_ADMIN']}><SettingsPage /></Protected>} />
        <Route path="/tenants" element={<Protected allow={['SUPER_ADMIN']}><TenantsPage /></Protected>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}
